import React, { FC, useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { VerifyTermApi } from "../../../../../../../../services/Api/Organization/organizationTypes";
import {
	SubscriptionInvoiceApi,
	SubscriptionTotalApi,
	SubscriptionVisibility,
	SubscriptionsApi,
} from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { OrganizationMeApi } from "../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import EmptySubscriptionsPreview from "./EmptySubscriptionsPreview";
import SingleSubscriptionPreview from "./SingleSubscriptionPreview";
import SubscriptionTotalPrice from "./SubscriptionTotalPrice";
import SubscriptionsInvoice from "./SubscriptionsInvoice";
import SubscriptionsInvoiceDetails from "./SubscriptionsInvoiceDetails";
import SubscriptionsPayment from "./SubscriptionsPayment";
import SubscriptionsSupport from "./SubscriptionsSupport";
import { areAllSubscriptionsCancelled } from "./utils";

export const isDiscountedSubscription = (totals: SubscriptionTotalApi[]) => {
	const yearlyTotalPrice = totals.find((total) => total.billing_period === "YEARLY");
	const monthlyTotalPrice = totals.find((total) => total.billing_period === "MONTHLY");
	if (
		(yearlyTotalPrice && yearlyTotalPrice.discount_price && yearlyTotalPrice.discount_price.amount) ||
		(monthlyTotalPrice && monthlyTotalPrice.discount_price && monthlyTotalPrice.discount_price.amount)
	) {
		return true;
	}
	return false;
};
export const drawSubscriptionsPriceByType = (totals: SubscriptionTotalApi[], type: string) => {
	const yearlyPrice = totals.find((total) => total.billing_period === "YEARLY");
	const monthlyPrice = totals.find((total) => total.billing_period === "MONTHLY");

	let monthlyTotalPrice;
	if (monthlyPrice) {
		switch (type) {
			case "discount":
				monthlyTotalPrice = monthlyPrice.discount_price;
				break;
			case "sub_total":
				monthlyTotalPrice = monthlyPrice.sub_total_price;
				break;
			default:
				monthlyTotalPrice = monthlyPrice.total_price;
				break;
		}
	}
	let yearlyTotalPrice;
	if (yearlyPrice) {
		switch (type) {
			case "discount":
				yearlyTotalPrice = yearlyPrice.discount_price;
				break;
			case "sub_total":
				yearlyTotalPrice = yearlyPrice.sub_total_price;
				break;
			default:
				yearlyTotalPrice = yearlyPrice.total_price;
				break;
		}
	}
	if (yearlyTotalPrice) {
		return (
			<>
				<p className="mb-0 text-muted addon-heading">{FormatMoney(yearlyTotalPrice)}/rok</p>
				{monthlyTotalPrice && (
					<span className={"text-muted addon-heading"}>+ {FormatMoney(monthlyTotalPrice)}/mies.</span>
				)}
			</>
		);
	} else if (monthlyTotalPrice)
		return <p className={"text-muted addon-heading"}>{FormatMoney(monthlyTotalPrice)}/mies.</p>;
};

interface Props {
	subscriptions: SubscriptionsApi;
	meDetails: OrganizationMeApi;
	onFetchOrganizationMeDetails?: () => void;
	invoices: SubscriptionInvoiceApi[];
	subscriptionsVisibility: SubscriptionVisibility;
	unpaidProformas: SubscriptionInvoiceApi[];
	termsToAccept: VerifyTermApi[];
}

const SubscriptionsPreview: FC<Props> = ({
	subscriptions,
	meDetails,
	invoices,
	subscriptionsVisibility,
	unpaidProformas,
	termsToAccept,
}) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const [updatedMeDetails, setUpdatedMeDetails] = useState<OrganizationMeApi>(meDetails);
	const blockedUnpaidProformas = unpaidProformas.filter((x) => x.blocked);

	return (
		<div className={`subscriptions-preview`}>
			{subscriptionsVisibility !== SubscriptionVisibility.HIDDEN &&
				(subscriptions?.subscriptions.length > 0 ? (
					<Card className={`subscriptions`}>
						<Card.Body>
							<div className={`table-responsive`}>
								<table className={"table table-preview"}>
									<thead>
										<tr>
											<th>{t("modules.subscription.field.app.title")}</th>
											<th>{t("lib:common.word.status")}</th>
											<th>{t("modules.subscription.field.additions.title")}</th>
											<th>{t("modules.subscription.field.price_net.title")}</th>
										</tr>
									</thead>
									<tbody>
										{subscriptions.subscriptions.map((sub, index) => (
											<SingleSubscriptionPreview sub={sub} key={index} />
										))}
									</tbody>
								</table>
							</div>
							<h5>{t("lib:common.word.summary")}</h5>
							<div className={"subscriptions-summary"}>
								<h6>{t("modules.subscription.field.price_net.title")}</h6>
								<div className="d-flex flex-column align-items-end">
									<SubscriptionTotalPrice totals={subscriptions.totals} />
								</div>
							</div>
							{blockedUnpaidProformas.length > 0 && (
								<span className="text-danger">
									{t("modules.subscription.field.unpaid_proformas_description.title")}
								</span>
							)}
							{blockedUnpaidProformas.length === 0 &&
								termsToAccept.length === 0 &&
								subscriptionsVisibility === SubscriptionVisibility.VISIBLE &&
								subscriptions.customer.status === "ACTIVE" && (
									<Link className={"btn btn-primary"} to={`/${organization.id}/subscriptions/edit`}>
										{areAllSubscriptionsCancelled(subscriptions.subscriptions)
											? t("lib:common.action.activate")
											: t("modules.subscription.action.change_subscription.title")}
									</Link>
								)}
							{blockedUnpaidProformas.length === 0 &&
								termsToAccept.length === 0 &&
								subscriptionsVisibility === SubscriptionVisibility.VISIBLE &&
								subscriptions.customer.status === "NEW" && (
									<Link className={"btn btn-primary"} to={`/${organization.id}/subscriptions/edit`}>
										{t("modules.subscription.action.activate_subscription.title")}
									</Link>
								)}
						</Card.Body>
					</Card>
				) : (
					<EmptySubscriptionsPreview subscriptionsVisibility={subscriptionsVisibility} />
				))}
			<div className={"row"}>
				<div className={"col-md-6 mb-3"}>
					<SubscriptionsPayment invoices={invoices} />
				</div>
				<div className={"col-md-6 mb-3"}>
					<SubscriptionsInvoiceDetails
						meDetails={updatedMeDetails}
						customer={subscriptions.customer}
						onFetchOrganizationMeDetails={(data) => setUpdatedMeDetails(data)}
					/>
				</div>
				<div className={"col-md-6 mb-3"}>
					<SubscriptionsInvoice
						unPaidProformas={unpaidProformas}
						invoices={invoices}
						subscriptionsVisibility={subscriptionsVisibility}
					/>
				</div>
				<div className={"col-md-6 mb-3"}>
					<SubscriptionsSupport />
				</div>
			</div>
		</div>
	);
};
export default SubscriptionsPreview;
